import { request } from '@mairistem/tools';
import type {} from '@redux-devtools/extension';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getRegieIndicateurs } from '@/routes/regie';
import type { CacheMetadataType, IndicateursCleType } from '@/types';
import { CACHE_TTL, isCacheValid } from '@/utils/cache';

type IndicateursStoreType = {
  data: IndicateursCleType | null;
  loading: boolean;
  /** Requête en cours, y compris lors d'un rafraîchissement silencieux */
  fetching: boolean;
  error: string | null;
  cache: CacheMetadataType;

  fetch: (regieId: number, force?: boolean) => Promise<void>;
  clear: () => void;
};

const useIndicateursStore = create<IndicateursStoreType>()(
  devtools(
    (set, get) => ({
      data: null,
      loading: false,
      fetching: false,
      error: null,
      cache: {
        timestamp: null,
        regieId: null,
      },

      fetch: async (regieId: number, force = false) => {
        // Guard : éviter fetch concurrent
        if (get().fetching) return;

        // Skip si cache valide et pas de force
        const { cache } = get();
        if (
          !force &&
          isCacheValid(cache.timestamp, CACHE_TTL.INDICATEURS, cache.regieId, regieId)
        ) {
          return;
        }

        // Ne montrer le loading que si on n'a pas encore de données
        // Pour un refresh invisible à l'utilisateur
        const hasData = get().data !== null;
        set({ loading: !hasData, fetching: true, error: null });

        try {
          // Note: l'API utilise les headers pour identifier la régie
          const data: IndicateursCleType = await request.get(getRegieIndicateurs());
          set({
            data,
            loading: false,
            fetching: false,
            cache: {
              timestamp: Date.now(),
              regieId,
            },
          });
        } catch (error) {
          set({
            loading: false,
            fetching: false,
            error: (error as Error).message || 'Erreur lors du chargement des indicateurs',
          });
        }
      },

      clear: () => {
        set({
          data: null,
          loading: false,
          fetching: false,
          error: null,
          cache: { timestamp: null, regieId: null },
        });
      },
    }),
    { name: 'IndicateursStore' },
  ),
);

export default useIndicateursStore;
